const getValue = (
    name: string,
    valueType: 'string' | 'number' | 'boolean',
): boolean | number | string | undefined => {
    const value = process.env[name];
    if (value === undefined && process.env.NODE_ENV !== 'test') {
        throw new Error(`Environment variable ${name} is not defined`);
    }

    const parse =
        valueType === 'boolean'
            ? (v?: string) => (v === 'true' ? true : v === 'false' ? false : undefined)
            : valueType === 'number'
              ? (v?: string) => Number(v ?? undefined)
              : (v?: string) => v;

    const parsed = parse(value);
    if (parsed === undefined && process.env.NODE_ENV !== 'test') {
        throw new Error(`Environment variable ${name} is invalid`);
    }

    return parsed;
};

const isValid = (variable: {
    value: unknown;
    type: string;
}): variable is {
    value: string | number | boolean;
    type: 'string' | 'number' | 'boolean';
} => {
    return variable.type === 'string' || variable.type === 'boolean' || variable.type === 'number';
};

/**
 * Creates a helper object that strongly types any environment variables you want to expose.
 * @example
 * const envronment = getEnvironment({
 *  REACT_APP_API_URL: "",
 *  REACT_APP_URL: "",
 *  REACT_APP_TIMEOUT_MS: 0,
 * })
 */
export const getEnvironment = <T, R extends { [key in keyof T]: () => T[key] }>(variables: {
    [key in keyof T]: T[key];
}): R => {
    return Object.entries(variables).reduce((result: R, [key, defaultValue]) => {
        const defaultProps = {
            value: defaultValue,
            type: typeof defaultValue,
        };
        if (!isValid(defaultProps)) {
            throw new Error(
                `Invalid default value for environment variable ${key}: ${typeof defaultValue}`,
            );
        }
        return {
            ...result,
            [key]: () => getValue(key, defaultProps.type),
        };
    }, {} as R);
};

export const environment = getEnvironment({
    REACT_APP_API_AUDIENCE: '',
    REACT_APP_API_URL: '',
    REACT_APP_ENV: '',
    REACT_APP_IDP_CLIENT_ID: '',
    REACT_APP_IDP_ENV: '',
    REACT_APP_IDP_URL: '',
    REACT_APP_URL: '',
    REACT_APP_SEGMENT_WRITEKEY: '',
    REACT_APP_OPTIMIZELY_KEY: '',
    REACT_APP_VERSION: '',
    REACT_APP_DATADOG_CLIENT_TOKEN: '',
    REACT_APP_SENTRY_DSN: '',
    REACT_APP_SENTRY_RELEASE: '',
    REACT_APP_SERVICE: '',
});
